"use client"

import type React from "react"
import { useState, useEffect } from "react"
import { observer } from "mobx-react-lite"
import { Plus, Search } from "lucide-react"
import { useAppContext } from "../../../../shared/functions/Context"
import showModalFromId from "../../../../shared/functions/ModalShow"
import Modal from "../../../../shared/components/Modal"
import MODAL_NAMES from "../../../dialogs/ModalName"
import CreateNewTemplateModal from "../modals/CreateTemplateModal"
import CriteriaBox from "./CriteriaBox"


const Templates = observer(() => {
  const { api, store } = useAppContext()
  const [loading, setLoading] = useState(false)
  const [searchQuery, setSearchQuery] = useState("")

  const templates = store.templates.all.map((template) => template.asJson)

  const filteredTemplates = templates.filter((template) =>
    template.title.toLowerCase().includes(searchQuery.toLowerCase()),
  )

  useEffect(() => {
    const fetchTemplates = async () => {
      setLoading(true)
      await api.user.getAll()
      await api.templates.getAll()
      setLoading(false)
    }
    fetchTemplates()
  }, [api.user, api.templates])

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value)
  }

  const onCreateTemplate = () => {
    showModalFromId(MODAL_NAMES.THREE_SIXTY_FEEDBACK.CREATE_TEMPLATE_MODAL)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 w-full">
        <div className="relative">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-200"></div>
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent absolute top-0 left-0"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="w-full space-y-6">
      {/* Header Section */}
      <div className="flex flex-col gap-4 p-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl border border-blue-100">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h2 className="text-2xl font-bold text-slate-800 mb-2">Customized Templates</h2>
            <p className="text-slate-600">Create and manage evaluation templates</p>
          </div>
          <button
            onClick={onCreateTemplate}
            className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold px-6 py-3 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
          >
            <Plus className="h-5 w-5" />
            Create Template
          </button>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-400" />
          <input
            type="text"
            placeholder="Search templates..."
            value={searchQuery}
            onChange={handleSearchChange}
            className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-slate-700 placeholder-slate-400"
          />
        </div>
      </div>

      <Modal modalId={MODAL_NAMES.THREE_SIXTY_FEEDBACK.CREATE_TEMPLATE_MODAL}>
        <CreateNewTemplateModal />
      </Modal>

      {filteredTemplates.length > 0 ? (
        <CriteriaBox value={filteredTemplates} />
      ) : (
        <div className="text-center py-16 bg-white rounded-2xl border border-slate-200 shadow-sm">
          <div className="max-w-md mx-auto">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Plus className="h-8 w-8 text-blue-600" />
            </div>
            <h3 className="text-xl font-semibold text-slate-800 mb-2">
              {searchQuery ? "No Templates Found" : "No Templates Available"}
            </h3>
            <p className="text-slate-600 mb-6">
              {searchQuery
                ? "Try adjusting your search query to find what you're looking for."
                : "Create your first template to get started with evaluations."}
            </p>
            {!searchQuery && (
              <button
                onClick={onCreateTemplate}
                className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold px-6 py-3 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <Plus className="h-5 w-5" />
                Create Your First Template
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  )
})

export default Templates
